/**
 * @fileoverview The two doors on Today's start card ("Your garden starts
 * here"): a photo of anything, or type a plant (B96, the owner's "2 spots, a
 * photo and a typed"). B30, design revisions 1, 4 and 5 (the photo door opens
 * the photo picker on Today and shows small photos in place of the icon; the
 * type door opens the add form on Today). B80: every photo goes down the one
 * shared photo path, so a photo of anything is understood (packets are read as
 * packets there) and none is refused; B96 then folded the two photo doors that
 * opened that same picker into one, "A photo of anything".
 *
 * The order, the words' keys and where each door lands are fixed here so a
 * test can show them; WelcomePlace draws them. Nothing is decided here that
 * the server should decide: this is display order and wording only.
 *
 * Where: WelcomePlace (Today's start card); AddWays imports aiKnownAllowed.
 * Words (en/es): welcome.place.* in the locale files.
 *
 * @module welcome/lib/startDoors
 */

/** Which door: a photo of anything (packets, a seed order, plants growing), or the add form. */
export type StartDoorId = 'photo' | 'type';

/** Where a door takes the gardener. */
export type StartDoorLands =
  /** The photo picker on Today; the photos go to the tray, where the server's guess says what each one is (B30 rev 4; B80). */
  | { kind: 'photoPicker' }
  /** The add-a-plant form, over Today (B30 rev 5); Today owns it. */
  | { kind: 'addForm' };

/** One door on the start card. */
export interface StartDoor {
  /** Which door. */
  id: StartDoorId;
  /** The locale key of the door's title. */
  titleKey: string;
  /** The locale key of the door's hint. */
  hintKey: string;
  /** The locale key of the hint for a gardener whose plan gets no photo reading, or whose plan is not known yet (the photo door). */
  hintKeyNoAi?: string;
  /** Drawn with the primary border (the first door). */
  first?: boolean;
  /** Small photos drawn in place of the icon (one, or two fanned), paths under public/ (decorative); none keeps the icon. */
  thumbs?: readonly string[];
  /** Where it lands. */
  lands: StartDoorLands;
}

/** The two doors, in the order they are shown (B96). */
export const START_DOORS: readonly StartDoor[] = [
  {
    id: 'photo',
    titleKey: 'welcome.place.anyPhoto',
    hintKey: 'welcome.place.anyPhotoHint',
    // Free gets no photo reading, so it gets a hint that makes no promise of a name (D70 F4); so does a plan not yet known (C77).
    hintKeyNoAi: 'welcome.place.anyPhotoHintNoAi',
    first: true,
    // Both start photos, fanned: "anything" shown as more than one kind.
    thumbs: ['/start/seed-packets.jpg', '/start/plants-growing.jpg'],
    // B80: the one shared photo path, so any photo is understood and none is refused; a packet photo is read as packets there.
    lands: { kind: 'photoPicker' },
  },
  {
    id: 'type',
    titleKey: 'welcome.place.addPlant',
    hintKey: 'welcome.place.addPlantHint',
    lands: { kind: 'addForm' },
  },
];

/**
 * The locale key for a door's hint.
 *
 * @param door - The door.
 * @param aiAllowed - Whether the gardener's plan includes the photo reading.
 * @returns The hint key: the plain one, or the no-reading one when the plan has none.
 */
export function doorHintKey(door: StartDoor, aiAllowed: boolean): string {
  return !aiAllowed && door.hintKeyNoAi ? door.hintKeyNoAi : door.hintKey;
}

/**
 * Whether the photo-reading hint may be shown: only once the plan is known and includes the reading. While the billing
 * answer is still on its way, useAiAccess().allowed reads true (so the app never looks locked), but the start card must
 * not promise names a Free gardener will not get, so it shows the Free words until access is known.
 *
 * @param access - useAiAccess()'s answer: allowed, and whether it is still loading.
 * @param access.allowed - False only when the server has said the plan has no photo reading.
 * @param access.loading - True while the answer is not known yet.
 * @returns True only when access is known and allowed.
 */
export function aiKnownAllowed(access: { allowed: boolean; loading: boolean }): boolean {
  return !access.loading && access.allowed;
}
